import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, AlertCircle, CheckCircle2, PauseCircle } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { Tenant } from '@/types';

export function Tenants() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isSuperAdmin = api.isSuperAdmin();

  useEffect(() => {
    api.listTenants()
      .then(setTenants)
      .catch((e) => setError(e instanceof ApiClientError ? e.message : 'Failed to load tenants'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Tenants</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">Manage organisations and their usage quotas</p>
        </div>
        {isSuperAdmin && (
          <Link to="/tenants/new" className="btn-primary self-start sm:self-auto">
            <Plus size={16} className="mr-2" />
            Add Tenant
          </Link>
        )}
      </div>

      {loading && <div className="text-sm text-gray-500 dark:text-gray-400 py-12 text-center">Loading…</div>}

      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-800 dark:text-red-400 text-sm">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}

      {!loading && !error && tenants.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Users size={40} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="font-semibold text-gray-900 dark:text-white mb-1">No tenants yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Add your first organisation to start issuing widget tokens.</p>
          {isSuperAdmin && (
            <Link to="/tenants/new" className="btn-primary">
              <Plus size={15} className="mr-2" /> Add Tenant
            </Link>
          )}
        </div>
      )}

      {tenants.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  <th className="px-4 py-3 text-left">Tenant</th>
                  <th className="px-4 py-3 text-left hidden sm:table-cell">Status</th>
                  <th className="px-4 py-3 text-right hidden md:table-cell">Users</th>
                  <th className="px-4 py-3 text-right hidden lg:table-cell">Max / Day</th>
                  <th className="px-4 py-3 text-right hidden lg:table-cell">Max / Month</th>
                  <th className="px-4 py-3 text-right hidden md:table-cell">Quota / User</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                {tenants.map((t) => {
                  const quotaPerUser = t.user_count
                    ? Math.floor(t.max_requests_per_day / t.user_count)
                    : t.max_requests_per_day;
                  return (
                    <tr key={t.tenant_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-900 dark:text-white">{t.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">{t.tenant_id}</div>
                        {/* Status badge inline on mobile */}
                        <div className="sm:hidden mt-1.5">
                          <StatusBadge status={t.status} />
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden sm:table-cell">
                        <StatusBadge status={t.status} />
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums hidden md:table-cell">
                        <span className={t.user_count === t.max_users ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-gray-900 dark:text-white'}>
                          {t.user_count ?? 0}
                        </span>
                        <span className="text-gray-400 dark:text-gray-500"> / {t.max_users}</span>
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums text-gray-700 dark:text-gray-300 hidden lg:table-cell">
                        {t.max_requests_per_day.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums text-gray-700 dark:text-gray-300 hidden lg:table-cell">
                        {t.max_requests_per_month.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums text-gray-700 dark:text-gray-300 hidden md:table-cell">
                        {quotaPerUser.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <Link
                          to={`/tenants/${t.tenant_id}`}
                          className="text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium"
                        >
                          View →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: Tenant['status'] }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
        <CheckCircle2 size={11} /> Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
      <PauseCircle size={11} /> Suspended
    </span>
  );
}
